import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Play, Pause, RotateCcw } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import SiriWave from "siriwave";

const SiriWaveTestPage: React.FC = () => {
  const [siriWave, setSiriWave] = useState<SiriWave | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [amplitude, setAmplitude] = useState([1]);
  const [speed, setSpeed] = useState([0.2]);
  const [frequency, setFrequency] = useState([6]);
  const [color, setColor] = useState("#10b981");
  const [style, setStyle] = useState<"ios" | "ios9">("ios9");
  const [cover, setCover] = useState(true);
  const [autostart, setAutostart] = useState(false);
  
  // Audio input state
  const [audioPermissionGranted, setAudioPermissionGranted] = useState(false);
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  const [audioInputMode, setAudioInputMode] = useState(false);
  const [microphoneLevel, setMicrophoneLevel] = useState(0);
  const [requestingPermission, setRequestingPermission] = useState(false);

  const siriWaveRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Initialize SiriWave
  const initializeSiriWave = () => {
    if (siriWaveRef.current) {
      // Dispose existing wave
      if (siriWave) {
        siriWave.dispose();
      }

      console.log("Initializing SiriWave with config:", {
        width: 400,
        height: 150,
        style,
        amplitude: amplitude[0],
        speed: speed[0],
        frequency: frequency[0],
        color,
        cover,
        autostart,
      });

      try {
        const wave = new SiriWave({
          container: siriWaveRef.current,
          width: 400,
          height: 150,
          style,
          amplitude: amplitude[0],
          speed: speed[0],
          frequency: frequency[0],
          color,
          cover,
          autostart,
        });

        setSiriWave(wave);
        setIsRunning(autostart);
        console.log("SiriWave initialized successfully:", wave);
      } catch (error) {
        console.error("Error initializing SiriWave:", error);
      }
    }
  };

  // Initialize on mount and when settings change
  useEffect(() => {
    initializeSiriWave();
    
    return () => {
      if (siriWave) {
        siriWave.dispose();
      }
    };
  }, [style, cover, autostart]);

  // Handle audio input mode changes
  useEffect(() => {
    if (audioInputMode && audioPermissionGranted && isRunning) {
      startAudioMonitoring();
    } else {
      stopAudioMonitoring();
    }
  }, [audioInputMode, audioPermissionGranted, isRunning, analyser, siriWave]);

  // Cleanup audio resources on unmount
  useEffect(() => {
    return () => {
      stopAudioMonitoring();
      if (audioStream) {
        audioStream.getTracks().forEach(track => track.stop());
      }
      if (audioContext && audioContext.state !== 'closed') {
        audioContext.close();
      }
    };
  }, [audioStream, audioContext]);

  // Update wave properties when sliders change
  useEffect(() => {
    if (siriWave) {
      siriWave.setAmplitude(amplitude[0]);
    }
  }, [amplitude, siriWave]);

  useEffect(() => {
    if (siriWave) {
      siriWave.setSpeed(speed[0]);
    }
  }, [speed, siriWave]);

  useEffect(() => {
    if (siriWave) {
      // SiriWave doesn't have a direct setFrequency method
      // We need to recreate the wave for frequency changes
      initializeSiriWave();
    }
  }, [frequency]);

  useEffect(() => {
    if (siriWave) {
      // Color changes also require reinitializing
      initializeSiriWave();
    }
  }, [color]);

  const toggleAnimation = () => {
    if (siriWave) {
      if (isRunning) {
        siriWave.stop();
        setIsRunning(false);
        stopAudioMonitoring();
        console.log("SiriWave stopped");
      } else {
        siriWave.start();
        setIsRunning(true);
        if (audioInputMode && audioPermissionGranted) {
          startAudioMonitoring();
        }
        console.log("SiriWave started");
      }
    }
  };

  const resetSettings = () => {
    setAmplitude([1]);
    setSpeed([0.2]);
    setFrequency([6]);
    setColor("#10b981");
    setStyle("ios9");
    setCover(true);
    setAutostart(false);
    setAudioInputMode(false);
    stopAudioMonitoring();
  };

  // Request audio permission
  const requestAudioPermission = async () => {
    setRequestingPermission(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setAudioStream(stream);
      setAudioPermissionGranted(true);
      
      // Create audio context and analyser
      const context = new AudioContext();
      const analyserNode = context.createAnalyser();
      analyserNode.fftSize = 256;
      
      const source = context.createMediaStreamSource(stream);
      source.connect(analyserNode);
      
      setAudioContext(context);
      setAnalyser(analyserNode);
      
      console.log('Audio permission granted and audio context created');
    } catch (error) {
      console.error('Error requesting audio permission:', error);
      alert('Audio permission is required to use real audio input. Please allow microphone access.');
    } finally {
      setRequestingPermission(false);
    }
  };

  // Start real-time audio monitoring
  const startAudioMonitoring = () => {
    if (!analyser || !siriWave || !isRunning) return;
    
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    
    const updateAmplitude = () => {
      if (!analyser || !siriWave || !isRunning || !audioInputMode) return;
      
      analyser.getByteFrequencyData(dataArray);
      
      // Calculate average audio level
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      
      // Scale the amplitude for SiriWave (0.1 to 4)
      const scaledAmplitude = Math.max(0.1, Math.min(4, average / 30));
      
      // Update microphone level for display
      setMicrophoneLevel(Math.round(average));
      
      // Set wave amplitude based on audio input
      siriWave.setAmplitude(scaledAmplitude);
      
      // Continue monitoring
      animationFrameRef.current = requestAnimationFrame(updateAmplitude);
    };
    
    updateAmplitude();
  };

  // Stop audio monitoring
  const stopAudioMonitoring = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  // Toggle between simulation and real audio input
  const toggleAudioInputMode = () => {
    if (!audioPermissionGranted) {
      requestAudioPermission();
      return;
    }
    
    setAudioInputMode(!audioInputMode);
  };

  // Simulate audio levels for testing
  const simulateAudioLevels = () => {
    if (!siriWave || !isRunning || audioInputMode) return;

    const simulate = () => {
      const randomAmplitude = Math.random() * 3 + 0.5; // Random between 0.5 and 3.5
      siriWave.setAmplitude(randomAmplitude);
      
      setTimeout(() => {
        if (siriWave && isRunning && !audioInputMode) {
          simulate();
        }
      }, 100 + Math.random() * 200); // Random interval between 100-300ms
    };

    simulate();
  };

  return (
    <div className="min-h-screen bg-muted p-8">
      <div className="max-w-6xl mx-auto space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            SiriWave Test Page
          </h1>
          <p className="text-muted-foreground">
            Test and configure SiriWave animations with real-time controls
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* SiriWave Display */}
          <Card>
            <CardHeader>
              <CardTitle>SiriWave Animation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* SiriWave Container */}
              <div className="flex justify-center">
                <div className="bg-black rounded-lg p-4 border border-border">
                  <div
                    ref={siriWaveRef}
                    className="flex items-center justify-center"
                    style={{ width: "400px", height: "150px" }}
                  />
                </div>
              </div>

              {/* Control Buttons */}
              <div className="flex justify-center gap-4">
                <Button
                  onClick={toggleAnimation}
                  variant={isRunning ? "destructive" : "default"}
                  size="lg"
                >
                  {isRunning ? (
                    <>
                      <Pause className="w-4 h-4 mr-2" />
                      Stop
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" />
                      Start
                    </>
                  )}
                </Button>

                <Button
                  onClick={toggleAudioInputMode}
                  variant={audioInputMode ? "default" : "secondary"}
                  disabled={requestingPermission || (!audioPermissionGranted && !requestingPermission)}
                >
                  {requestingPermission ? "Requesting..." : 
                   audioInputMode ? "🎤 Real Audio" : 
                   audioPermissionGranted ? "Use Real Audio" : "Request Mic Access"}
                </Button>

                <Button
                  onClick={simulateAudioLevels}
                  variant="secondary"
                  disabled={!isRunning || audioInputMode}
                >
                  Simulate Audio
                </Button>

                <Button onClick={resetSettings} variant="outline">
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset
                </Button>
              </div>

              {/* Status */}
              <div className="text-center text-sm text-muted-foreground">
                <div>Status: {isRunning ? "🟢 Running" : "🔴 Stopped"}</div>
                {audioPermissionGranted && (
                  <div className="mt-1">
                    Audio: {audioInputMode ? `🎤 Live Input (Level: ${microphoneLevel})` : "📊 Simulation"}
                  </div>
                )}
                {!audioPermissionGranted && (
                  <div className="mt-1 text-mid">
                    ⚠️ Microphone access not granted
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Controls */}
          <Card>
            <CardHeader>
              <CardTitle>Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Style Selection */}
              <div className="space-y-2">
                <Label>Style</Label>
                <Select value={style} onValueChange={(value: "ios" | "ios9") => setStyle(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ios">iOS</SelectItem>
                    <SelectItem value="ios9">iOS 9</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Color Selection */}
              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    className="w-12 h-10 rounded border border-border"
                  />
                  <div className="flex-1">
                    <Select value={color} onValueChange={setColor}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="#10b981">Green</SelectItem>
                        <SelectItem value="#3b82f6">Blue</SelectItem>
                        <SelectItem value="#ef4444">Red</SelectItem>
                        <SelectItem value="#f59e0b">Orange</SelectItem>
                        <SelectItem value="#8b5cf6">Purple</SelectItem>
                        <SelectItem value="#ec4899">Pink</SelectItem>
                        <SelectItem value="#ffffff">White</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Amplitude */}
              <div className="space-y-2">
                <Label>Amplitude: {amplitude[0]}</Label>
                <Slider
                  value={amplitude}
                  onValueChange={setAmplitude}
                  max={5}
                  min={0.1}
                  step={0.1}
                  className="w-full"
                />
              </div>

              {/* Speed */}
              <div className="space-y-2">
                <Label>Speed: {speed[0]}</Label>
                <Slider
                  value={speed}
                  onValueChange={setSpeed}
                  max={1}
                  min={0.05}
                  step={0.05}
                  className="w-full"
                />
              </div>

              {/* Frequency */}
              <div className="space-y-2">
                <Label>Frequency: {frequency[0]}</Label>
                <Slider
                  value={frequency}
                  onValueChange={setFrequency}
                  max={20}
                  min={1}
                  step={1}
                  className="w-full"
                />
              </div>

              {/* Switches */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="cover">Cover</Label>
                  <Switch
                    id="cover"
                    checked={cover}
                    onCheckedChange={setCover}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="autostart">Autostart</Label>
                  <Switch
                    id="autostart"
                    checked={autostart}
                    onCheckedChange={setAutostart}
                  />
                </div>
              </div>

              {/* Current Config Display */}
              <div className="mt-6 p-3 bg-muted rounded text-sm">
                <h4 className="font-medium mb-2">Current Configuration:</h4>
                <pre className="text-xs">
{JSON.stringify({
  style,
  amplitude: amplitude[0],
  speed: speed[0],
  frequency: frequency[0],
  color,
  cover,
  autostart,
  isRunning,
  audioInputMode,
  audioPermissionGranted,
  microphoneLevel
}, null, 2)}
                </pre>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Usage Instructions */}
        <Card>
          <CardHeader>
            <CardTitle>Usage Instructions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>1. <strong>Start/Stop:</strong> Use the play/pause button to control the animation</p>
              <p>2. <strong>Amplitude:</strong> Controls the height of the waves (0.1 to 5)</p>
              <p>3. <strong>Speed:</strong> Controls how fast the waves move (0.05 to 1)</p>
              <p>4. <strong>Frequency:</strong> Controls the number of waves (1 to 20)</p>
              <p>5. <strong>Style:</strong> Choose between iOS and iOS 9 styles</p>
              <p>6. <strong>Color:</strong> Pick a color for the waves</p>
              <p>7. <strong>Cover:</strong> Whether the wave should cover the entire container</p>
              <p>8. <strong>Autostart:</strong> Whether the animation should start automatically</p>
              <p>9. <strong>Real Audio Input:</strong> Use your microphone to control wave amplitude in real-time</p>
              <p>10. <strong>Simulate Audio:</strong> Creates random amplitude changes to simulate voice activity</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SiriWaveTestPage;